/* eslint-disable react/jsx-key */
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import ProfileAvatar from "../common/ProfileAvatar";
import { dateCompareMostRecent } from "../common/util";
import { Col, Divider, Row } from "antd";
import moment from "moment";
import { getProfileIconLink } from "../../../utils/profile";
import { appNextUrl, constructUrlWithParams, decodeString } from 'findem-helpers';
import { useUrlParams } from "../../../utils/use-url-params";

type ShortlistedProfilesProps = {
  data: any
}

const ProfileWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const DivAsLink = styled.div`
  color: #1890ff;
  cursor: pointer;
  font-size: 12px;
  max-width: 400px;
`;

const PageHeader = styled.div`
  font-size: 20px;
  font-weight: 500;
  font-style: medium;
  margin-bottom: 16px;
  display: flex;
  flex-direction: row;
  gap: 32px;
`;

const BasicInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const WorkExp = styled.div`
  font-size: 12px;
  font-weight: 400;
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
`;

const WorkTitle = styled.div`
  font-weight: 400;
`;

const Head1 = styled.div`
  font-size: 12px;
  font-weight: 500;
`;

const ProfileDetails = (props: ShortlistedProfilesProps) => {
  const { data } = props;
  const { icpid, joid, accountId } = useUrlParams();

  const imgSource = useMemo(() => {
    return getProfileIconLink(data);
  }, [data]);

  const profileUrl = useMemo(() => {
    const params: { icpid?: string, joid?: string, aId?: string } = {};
    if (icpid) params.icpid = icpid;
    if (joid) params.joid = joid;
    if (accountId) params.aId = accountId;
    return constructUrlWithParams(`${appNextUrl}/profiles/${data.ref}`, params);
  }, [data.ref, icpid, joid, accountId]);


  const currentExps = useMemo(() =>
    data.workexp
      .filter(
        (we: any) =>
          (we.duration && we.duration.to_present === true) ||
          (we.projects &&
            we.projects.length > 0 &&
            we.projects[0].duration &&
            we.projects[0].duration.to_present === true)
      )
      .sort(dateCompareMostRecent), [data]);

  const sortedEducation = useMemo(() =>
    data.education?.sort(dateCompareMostRecent) || [], [data]);

  const sortedExps = useMemo(() =>
    data.workexp.sort(dateCompareMostRecent) || [], [data]);

  const onLinkClick = useCallback((e: any) => {
    e.preventDefault();
    window.open(e.currentTarget.getAttribute('data-href'), '_blank');
  }, []);

  return (<>
  <ProfileWrapperDiv>
    <Row>
      <PageHeader>
        <Col>
          <ProfileAvatar
            imageUrl={imgSource}
            fullName={data.candidate.full_name}
            imageHeight={70}
            imageWidth={70}
            fontSize={24}
          />
          <BasicInfoWrapper style={{marginTop: '16px'}}>
            <Name>{decodeString(data.candidate.full_name)}</Name>
            <WorkExp>{currentExps.map((exp) => (<Row style={{gap: '4px'}}>
                <WorkTitle>{decodeString(exp.projects[0]?.role_and_group?.title)}</WorkTitle>{' '} at {' '}<WorkTitle>{decodeString(exp.company?.name)}</WorkTitle>
              </Row>))}
            </WorkExp>
            <WorkExp>
              {data.candidate.address}
            </WorkExp>
          </BasicInfoWrapper>
          <BasicInfoWrapper>
            <Head1 style={{marginTop: '16px'}}>Profile Link</Head1>
            <DivAsLink onClick={onLinkClick} data-href={profileUrl}>{profileUrl}</DivAsLink>
          </BasicInfoWrapper>
          {data.candidate.linkedin && <BasicInfoWrapper>
            <Head1 style={{marginTop: '16px'}}>LinkedIn Link</Head1>
            <DivAsLink onClick={onLinkClick} data-href={data.candidate.linkedin}>{data.candidate.linkedin}</DivAsLink>
          </BasicInfoWrapper>}
        </Col>
        <BasicInfoWrapper>
          <Row>
            <Name>Experience</Name>
            <Divider style={{margin: '4px 0px'}}/>
            <WorkExp style={{gap: '4px'}}>{sortedExps.map((exp) => (
              <Row>
                {decodeString(exp.projects[0]?.role_and_group?.title)}{' '}at{' '}<Head1>{decodeString(exp.company?.name)}</Head1>
                  {' '}({exp.duration?.start_date ? moment(exp.duration?.start_date).format('MMM YYYY') : ''} - {exp.duration?.to_present ? 'Present' :exp.duration?.end_date ? moment(exp.duration.end_date).format('MMM YYYY') : ''})
              </Row>))}
            </WorkExp>
          </Row>
          <Row style={{marginTop: '16px'}}>
            <Name>Education</Name>
            <Divider style={{margin: '4px 0px'}}/>
            <WorkExp style={{gap: '4px'}}>{sortedEducation.map((edu) => (
              <Row>
                {edu.education_details?.degree.length > 0 ? decodeString(edu.education_details?.degree[0]) : '' }{' '}
                {edu.education_details?.major.length > 0 ? `, ${decodeString(edu.education_details?.major[0])}` : '' } at{' '}<Head1>{decodeString(edu.school_info?.name)}</Head1>
                  {' '}({edu.duration?.start_date ? moment(edu.duration.start_date).format('YYYY') : ''} - {edu.duration?.to_present ? 'Present' : edu.duration?.end_date ? moment(edu.duration.end_date).format('YYYY') : ''})
              </Row>))}
            </WorkExp>
          </Row>
        </BasicInfoWrapper>
      </PageHeader>
    </Row>
    {data.skills && (data.skills.length > 0) && <Row>
      <Row>
        <Name>Skills</Name>
        <Divider style={{margin: '4px 0px'}}/>
        <WorkExp>
          {decodeString(data.skills.join(', '))}
        </WorkExp>
      </Row>
    </Row>}
    {data.candidate.about_me && data.candidate.about_me !== 'undefined' && <Row>
      <Row style={{marginTop: '16px'}}>
        <Name>Bio</Name>
        <Divider style={{margin: '4px 0px'}}/>
        <WorkExp>
          {decodeString(data.candidate.about_me)}
        </WorkExp>
      </Row>
    </Row>}
  </ProfileWrapperDiv>
  </>);
}

export default ProfileDetails;
